import { useEffect, useRef, useState } from "react";
import CaseCard from "../components/results/CaseCard";
import ProjectCard from "../components/results/ProjectCard";
import CtaPanel from "../components/sections/CtaPanel";
import PageHero from "../components/sections/PageHero";
import SectionHeading from "../components/ui/SectionHeading";
import {
  caseStudyDetails,
  moreProjects,
  resultTrades,
  resultsPage,
} from "../data/results";
import { cx } from "../lib/cx";

const filters = [{ id: "all", label: "All" }, ...resultTrades];
const isFilter = (id) => filters.some((filter) => filter.id === id);
const headline = Object.entries(caseStudyDetails);
const more = Object.entries(moreProjects);

/**
 * The trade filter, mirrored in the URL hash (#trucking, …) so the home
 * page's "See more companies like yours" links open it pre-filtered.
 */
function useTradeFilter() {
  const [filter, setFilter] = useState("all");
  useEffect(() => {
    const fromHash = () => {
      const id = window.location.hash.slice(1);
      setFilter(isFilter(id) ? id : "all");
    };
    fromHash();
    window.addEventListener("hashchange", fromHash);
    return () => window.removeEventListener("hashchange", fromHash);
  }, []);
  const choose = (id) => {
    setFilter(id);
    const { pathname, search } = window.location;
    window.history.replaceState(
      null,
      "",
      pathname + search + (id === "all" ? "" : "#" + id),
    );
  };
  return [filter, choose];
}

/** Grid spans so a short last row of cards fills the width. */
function cardSpan(index, count) {
  const remainder = count % 3;
  const inLastRow = index >= count - remainder;
  if (remainder === 2 && inLastRow) return "lg:col-span-3";
  if (remainder === 1 && inLastRow) return "lg:col-span-2 lg:col-start-3";
  return "lg:col-span-2";
}

function FilterBar({ filter, onChange, count }) {
  const row = useRef(null);
  // Keep the chosen chip in view in the phone-width scrolling row.
  useEffect(() => {
    const chip = row.current?.querySelector('[aria-pressed="true"]');
    const { scrollWidth, clientWidth } = row.current || {};
    if (chip && scrollWidth > clientWidth)
      row.current.scrollTo({
        left: chip.offsetLeft - row.current.offsetLeft - 16,
        behavior: "smooth",
      });
  }, [filter]);
  return (
    <>
      {/* Targets for /case-studies/#<trade> links (the home page selector,
          case-study back links); the filter reads the same hash. */}
      {resultTrades.map(({ id }) => (
        <span key={id} id={id} aria-hidden="true" />
      ))}
      <div className="sticky top-[71px] z-40 bg-ink py-3 shadow-[0_6px_16px_-10px_rgba(0,0,0,0.6)] md:py-3.5">
        <div className="mx-auto flex max-w-280 items-center gap-2.5 px-4 sm:px-6">
          <span
            id="results-filter-label"
            className="tag shrink-0 font-semibold text-yellow max-sm:sr-only"
          >
            {resultsPage.filterLabel}
          </span>
          {/* One scrollable row on phones; wraps on wider screens. */}
          <div
            ref={row}
            role="group"
            aria-labelledby="results-filter-label"
            className="-my-1 flex min-w-0 gap-2.5 overflow-x-auto py-1 [scrollbar-width:none] max-sm:-mx-4 max-sm:px-4 md:flex-wrap md:overflow-visible"
          >
            {filters.map(({ id, label }) => (
              <button
                key={id}
                type="button"
                aria-pressed={filter === id}
                onClick={() => onChange(id)}
                className={cx(
                  "shrink-0 cursor-pointer rounded-lg border-2 px-3.5 py-1.5 text-[14.5px] font-bold whitespace-nowrap transition-colors md:px-4 md:py-2",
                  filter === id
                    ? "border-yellow bg-yellow text-ink"
                    : "border-white/30 text-white hover:border-yellow",
                )}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <p className="sr-only" aria-live="polite">
          {count === 1 ? "Showing 1 project" : `Showing ${count} projects`}
        </p>
      </div>
    </>
  );
}

export default function ResultsPage() {
  const [filter, setFilter] = useTradeFilter();
  const matches = ([, study]) => filter === "all" || study.trade === filter;
  const cases = headline.filter(matches);
  const projects = more.filter(matches);

  return (
    <>
      <PageHero
        eyebrow={resultsPage.eyebrow}
        title={resultsPage.title}
        lede={resultsPage.lede}
        image={resultsPage.image}
        imageAlt={resultsPage.imageAlt}
      />
      <FilterBar
        filter={filter}
        onChange={setFilter}
        count={cases.length + projects.length}
      />

      <section
        aria-label="Case studies"
        className="bg-surface px-4 pt-12 pb-16 sm:px-6 md:pt-14 md:pb-20"
      >
        <div className="mx-auto grid max-w-280 gap-8 md:gap-10">
          {cases.map(([path, study]) => (
            <CaseCard key={path} path={path} study={study} />
          ))}
        </div>
      </section>

      {projects.length > 0 && (
        <section className="bg-white px-4 py-16 sm:px-6 md:py-20">
          <div className="mx-auto max-w-280">
            <SectionHeading
              eyebrow={resultsPage.moreEyebrow}
              title={resultsPage.moreTitle}
              className="mb-10"
            >
              <p>{resultsPage.moreText}</p>
            </SectionHeading>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-6">
              {projects.map(([path, project], i) => (
                <ProjectCard
                  key={path}
                  path={path}
                  project={project}
                  className={cardSpan(i, projects.length)}
                  wide={cardSpan(i, projects.length) === "lg:col-span-3"}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      <CtaPanel
        title={resultsPage.cta.title}
        text={resultsPage.cta.text}
        className={projects.length ? "bg-surface" : "bg-white"}
      />
    </>
  );
}
